/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { RecordListInputConfig_Reference } from "@smartsuite/automation-engine-api";
import * as R from "remeda";
import { v4 as uuid } from "uuid";
import type { ActionListMerger } from ".";
import type {
  ActionCandidate,
  ActionGroupCandidate,
} from "../../../../types/candidate";
import {
  INPUT_APPLICATION_ID,
  INPUT_SOLUTION_ID,
} from "../../../../utils/constants";
import { buildStringInputConfig } from "../../../../utils/inputs";
import { rootStore } from "../../..";
import { collectInstanceIdsDeep } from "../../utils/action-group-walk";

const getInstanceId = (): number => {
  const allInstanceIds = collectInstanceIdsDeep(
    rootStore.automations.candidate.self.actionGroups,
  );
  return Math.max(-1, ...allInstanceIds) + 1;
};

export const mergeEmptyAction =
  (
    actionHash: string,
    integrationId: string,
    actionId: string,
  ): ActionListMerger =>
  (actions: ActionCandidate[]) => {
    const instanceId = getInstanceId();
    return [
      ...actions,
      {
        hash: actionHash,
        actionReference: {
          integrationId,
          actionId,
          instanceId,
        },
        credentialId: "",
        description: {
          actionId: "",
          label: "",
          subLabel: "",
          authenticationId: [],
          inputs: [],
          outputs: [],
          conditionFields: [],
        },
        inputs: [],
        dependencies: [],
      },
    ];
  };

export const addLoopActionGroup = (
  type: ActionGroupCandidate["config"]["oneofKind"],
): ActionGroupCandidate => {
  const instanceId = getInstanceId();

  switch (type) {
    default:
      return {
        instanceId,
        config: {
          oneofKind: "loop",
          loop: {
            name: "",
            actionGroups: [
              {
                instanceId: instanceId + 1,
                config: {
                  oneofKind: "actions",
                  actions: {
                    actions: [],
                  },
                },
              },
            ],
          },
        },
      };
  }
};

export const ejectActionReferences =
  (actionHash: string): ActionListMerger =>
  (actions: ActionCandidate[]) => {
    const currentAction = actions.find((action) => action.hash === actionHash);
    if (currentAction === undefined) {
      return actions;
    }
    return actions.map((action) => ({
      ...action,
      recordList:
        action.recordList === undefined
          ? undefined
          : action.recordList.recordList.oneofKind === "actionReference" &&
              action.recordList.recordList.actionReference.instanceId ===
                currentAction.actionReference?.instanceId
            ? {
                metadata: {},
                recordList: {
                  oneofKind: "reference",
                  reference: RecordListInputConfig_Reference.NO_RECORD_LIST,
                },
              }
            : action.recordList,
    }));
  };

export const ejectActionReferencesInCurrentAction =
  (actionHash: string): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    actions.map((action) => ({
      ...action,
      recordList:
        action.recordList === undefined
          ? undefined
          : action.recordList.recordList.oneofKind === "actionReference" &&
              action.hash === actionHash
            ? {
                metadata: {},
                recordList: {
                  oneofKind: "reference",
                  reference: RecordListInputConfig_Reference.NO_RECORD_LIST,
                },
              }
            : action.recordList,
    }));

export const resetAction =
  (actionHash: string): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((action) => {
        if (action.hash !== actionHash) {
          return action;
        }
        return {
          hash: actionHash,
          actionReference: action.actionReference,
          credentialId: "",
          description: {
            actionId: "",
            label: "",
            subLabel: "",
            authenticationId: [],
            inputs: [],
            outputs: [],
            conditionFields: [],
          },
          inputs: [],
          dependencies: [],
        };
      }),
    );

export const moveAction =
  (sourceIndex: number, destinationIndex: number): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe([...actions], (actions) => {
      const [sourceAction] = actions.splice(sourceIndex, 1);
      actions.splice(destinationIndex, 0, sourceAction);
      return actions;
    });

export const mergeDuplicateAction =
  (actionHash: string): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.find((a) => a.hash === actionHash),
      (sampleAction) =>
        sampleAction
          ? R.pipe(
              actions,
              R.flatMap((action) =>
                action.hash === actionHash
                  ? [
                      action,
                      {
                        ...JSON.parse(JSON.stringify(action)),
                        hash: uuid(),
                        actionReference: {
                          ...action.actionReference,
                          instanceId: getInstanceId(),
                        },
                      },
                    ]
                  : [action],
              ),
            )
          : actions,
    );

export const ejectAction =
  (actionHash: string): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.filter((a) => a.hash !== actionHash),
    );

export const mergeNewActionWithApplicationSolution =
  (
    actionHash: string,
    integrationId: string,
    actionId: string,
    applicationId: string,
    solutionId: string,
  ): ActionListMerger =>
  (actions: ActionCandidate[]) => [
    ...actions,
    {
      hash: actionHash,
      actionReference: {
        integrationId,
        actionId,
        instanceId: getInstanceId(),
      },
      credentialId: "",
      description: {
        actionId: "",
        label: "",
        subLabel: "",
        authenticationId: [],
        inputs: [],
        outputs: [],
        conditionFields: [],
      },
      inputs: [
        buildStringInputConfig(INPUT_SOLUTION_ID, solutionId),
        buildStringInputConfig(INPUT_APPLICATION_ID, applicationId),
      ],
      dependencies: [],
    },
  ];
